import React, {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

const PAGE_SIZE = 30;

function normalized(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function tokenScore(value, token) {
  if (!value || !token) return Number.POSITIVE_INFINITY;
  if (value === token) return 0;
  if (value.startsWith(token)) return 5 + value.length - token.length;

  const word = value.split(/[^a-z0-9]+/).find(part => part.startsWith(token));
  if (word) return 15 + word.length - token.length;

  const containedAt = value.indexOf(token);
  if (containedAt >= 0) return 30 + containedAt;

  let cursor = 0;
  let gaps = 0;
  for (const character of token) {
    const next = value.indexOf(character, cursor);
    if (next < 0) return Number.POSITIVE_INFINITY;
    gaps += next - cursor;
    cursor = next + 1;
  }
  return 60 + gaps + value.length - token.length;
}

export function fuzzyPickerOptions(options, query, group = '') {
  const tokens = normalized(query).split(/\s+/).filter(Boolean);
  return options
    .map((option, index) => {
      if (group && option.group !== group) return null;
      const fields = [
        option.label,
        option.group,
        option.meta,
        ...(option.keywords || []),
      ].map(normalized).filter(Boolean);
      const score = tokens.reduce((total, token) => {
        const best = Math.min(...fields.map(field => tokenScore(field, token)));
        return total + best;
      }, 0);
      if (!Number.isFinite(score)) return null;
      return { index, option, score };
    })
    .filter(Boolean)
    .sort((left, right) => (
      left.score - right.score
      || left.index - right.index
    ))
    .map(result => result.option);
}

function focusableElements(container) {
  if (!container) return [];
  return [...container.querySelectorAll(
    'button:not(:disabled), input:not(:disabled), select:not(:disabled), [href], [tabindex]:not([tabindex="-1"])',
  )].filter(element => !element.hasAttribute('hidden'));
}

export default function SearchPickerDialog({
  description,
  emptyMessage = 'No matching options.',
  multiple = false,
  onApply,
  onClose,
  options,
  returnFocusRef,
  searchLabel = 'Search by name, type, stat, or effect',
  selectedValues = [],
  title,
}) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef(null);
  const searchRef = useRef(null);
  const openerRef = useRef(null);
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [draft, setDraft] = useState(() => new Set(selectedValues));
  const groups = useMemo(() => [...new Set(
    options.map(option => option.group).filter(Boolean),
  )], [options]);
  const matches = useMemo(
    () => fuzzyPickerOptions(options, query, group),
    [group, options, query],
  );
  const visibleOptions = matches.slice(0, visibleCount);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [group, query]);

  useEffect(() => {
    openerRef.current = returnFocusRef?.current ?? document.activeElement;
    const appRoot = document.querySelector(
      '.admin-world-editor, .admin-settingspage',
    );
    const previousAriaHidden = appRoot?.getAttribute('aria-hidden');
    const wasInert = Boolean(appRoot?.inert);
    const previousOverflow = document.body.style.overflow;
    if (appRoot) {
      appRoot.inert = true;
      appRoot.setAttribute('aria-hidden', 'true');
    }
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => {
      searchRef.current?.focus({ preventScroll: true });
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      if (appRoot) {
        appRoot.inert = wasInert;
        if (previousAriaHidden === null) appRoot.removeAttribute('aria-hidden');
        else appRoot.setAttribute('aria-hidden', previousAriaHidden);
      }
      openerRef.current?.focus?.({ preventScroll: true });
    };
  }, [returnFocusRef]);

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = focusableElements(dialogRef.current);
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const toggle = value => {
    setDraft(current => {
      const next = new Set(current);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  if (!portalTarget) return null;

  return createPortal(
    <div
      className="world-editor-picker-backdrop"
      onPointerDown={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        aria-describedby={description ? descriptionId : undefined}
        aria-labelledby={titleId}
        aria-modal="true"
        className="world-editor-picker"
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <header className="world-editor-picker__header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description ? <p id={descriptionId}>{description}</p> : null}
          </div>
          <button
            aria-label={`Close ${title}`}
            className="world-editor-picker__close"
            type="button"
            onClick={onClose}
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div className="world-editor-picker__filters">
          <label className="form-control" htmlFor={`${titleId}-search`}>
            <span>{searchLabel}</span>
            <input
              id={`${titleId}-search`}
              ref={searchRef}
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
            />
          </label>
          {groups.length > 1 ? (
            <label className="form-control" htmlFor={`${titleId}-group`}>
              <span>Category</span>
              <select
                id={`${titleId}-group`}
                value={group}
                onChange={event => setGroup(event.target.value)}
              >
                <option value="">All categories</option>
                {groups.map(value => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </label>
          ) : null}
        </div>

        <p className="world-editor-picker__count" aria-live="polite">
          {matches.length} result{matches.length === 1 ? '' : 's'}
        </p>
        <div
          className="world-editor-picker__results"
          onScroll={event => {
            const list = event.currentTarget;
            if (
              list.scrollHeight - list.scrollTop - list.clientHeight < 80
              && visibleCount < matches.length
            ) {
              setVisibleCount(count => count + PAGE_SIZE);
            }
          }}
        >
          {visibleOptions.length === 0 ? (
            <p className="world-editor-picker__empty">{emptyMessage}</p>
          ) : (
            <ul>
              {visibleOptions.map(option => {
                const selected = draft.has(option.value);
                return (
                  <li key={option.value}>
                    {multiple ? (
                      <label className="world-editor-picker__option">
                        <input
                          checked={selected}
                          type="checkbox"
                          onChange={() => toggle(option.value)}
                        />
                        <span>
                          <strong>{option.label}</strong>
                          {option.meta ? <small>{option.meta}</small> : null}
                        </span>
                      </label>
                    ) : (
                      <button
                        aria-pressed={selected}
                        className="world-editor-picker__option"
                        type="button"
                        onClick={() => {
                          onApply([option.value]);
                          onClose();
                        }}
                      >
                        <span>
                          <strong>{option.label}</strong>
                          {option.meta ? <small>{option.meta}</small> : null}
                        </span>
                        {selected ? <em>Selected</em> : null}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="world-editor-picker__actions">
          {visibleCount < matches.length ? (
            <button
              className="button-secondary"
              type="button"
              onClick={() => setVisibleCount(count => count + PAGE_SIZE)}
            >
              Show more results
            </button>
          ) : <span />}
          <div>
            <button className="button-secondary" type="button" onClick={onClose}>
              Cancel
            </button>
            {multiple ? (
              <button
                className="button-primary"
                type="button"
                onClick={() => {
                  onApply([...draft]);
                  onClose();
                }}
              >
                Apply selection ({draft.size})
              </button>
            ) : null}
          </div>
        </footer>
      </section>
    </div>,
    portalTarget,
  );
}
